import { STOCK_TYPE } from 'constants/trading';
import tradingDataStore from 'lib/stores/tradingData';
import { showTradingModalWithAuth } from 'features/securities/AuthModal/utils';
import { withMaintenanceGuard } from 'global/Securities/utils/maintenance';
import { SellOrderType } from 'global/Securities/types/transaction';
import useOpeningAccountStore from 'global/Securities/stores/useOpeningAccountStore';
import { AuthModalStatus } from 'features/securities/AuthModal/types';
import { AUTH_ORDER_TYPE } from 'features/securities/AuthModal/constants';
import { SyariahReturnType } from 'global/TradingModal/hooks/useSyariah';
import alertMessage from 'global/AlertMessage';
import { PortoData, Tradeable, TradeCallbackParams } from '../types';
import { useChartbitStore } from '../chartbit.store';

export const clearPortoAndOrderLabel = () => {
  window.dispatchEvent(
    new CustomEvent('update-porto-list', {
      detail: [],
    }),
  );

  window.dispatchEvent(
    new CustomEvent('update-order-list', {
      detail: [],
    }),
  );
};

export const checkTradeableStatus = async (
  tradeSymbol: string,
  orderId?: string,
): Promise<Tradeable> => {
  if (tradeSymbol) {
    const emittenInfo =
      useChartbitStore.getState().companyDetail[tradeSymbol]?.data;
    const type = emittenInfo?.type_company || '';
    const emittenStatus = emittenInfo?.status || '';
    const tradeable = emittenInfo?.tradeable || false;
    const isRight = type.toLowerCase() === STOCK_TYPE.right;
    const isWarrant = type.toLowerCase() === STOCK_TYPE.waran;
    const isReksadana = type.toLowerCase() === STOCK_TYPE.reksadana;
    const isSuspended = emittenStatus === 'STATUS_SUSPENDED';
    const { status } = useOpeningAccountStore.getState();

    if (isSuspended || status !== AuthModalStatus.COMPLETED) {
      return { isTradeable: false, isAmendable: false, type_company: type };
    }

    let isTradeable = Boolean(tradeable);
    let isAmendable = false;
    if (orderId) {
      const { orderHistory } = tradingDataStore.getState();
      const order = orderHistory?.data?.find((o) => o.orderid === orderId);
      isAmendable = Boolean(order?.amend_action);
    }

    if (isRight) {
      const { data: right } = await tradingDataStore
        .getState()
        .checkRightTradeable();
      isTradeable = right?.is_tradeable;
    } else if (isWarrant) {
      const { data } = await tradingDataStore
        .getState()
        .getExercise(tradeSymbol);
      isTradeable = data?.is_tradeable;
    } else if (isReksadana) {
      isTradeable = false;
    }

    return { isTradeable, isAmendable, type_company: type };
  }

  return { isTradeable: false, isAmendable: false, type_company: '' };
};

export const getCurrentLot = (tradeSymbol: string) => {
  const { result } = tradingDataStore.getState().portfolio.data;
  const dayTradeResult = tradingDataStore.getState().portfolio.data.day_trade;
  const hasPorto = result?.find((port) => port.symbol === tradeSymbol);
  const hasDT = dayTradeResult?.find((port) => port.symbol === tradeSymbol);
  const regularLot = hasPorto?.balance_lot || 0;
  const dayTradeLot = hasDT?.balance_lot || 0;
  const lot = regularLot + dayTradeLot;

  return lot;
};

export const getPortoData = (sym: string, isDayTrade: boolean) => {
  const { result } = tradingDataStore.getState().portfolio.data;
  const dayTradeResult = tradingDataStore.getState().portfolio.data.day_trade;
  const newPortoData = isDayTrade
    ? dayTradeResult.find((res) => res.symbol === sym)
    : result.find((res) => res.symbol === sym);

  return newPortoData;
};

export const onOpenTrading = async (
  data: TradeCallbackParams,
  setPortoData: (data: PortoData | null) => void,
  syariahData: SyariahReturnType,
) => {
  withMaintenanceGuard(async () => {
    const {
      isUserSyariah,
      checkSyariahTradeableStatus,
      showSyariahRestrictionModal,
    } = syariahData;

    if (isUserSyariah) {
      const { data: emittenInfo } = await tradingDataStore
        .getState()
        .getCompany(data.symbol);
      const { indexes } = emittenInfo;
      const isSyariah =
        indexes.indexOf('JII') > -1 || indexes.indexOf('ISSI') > -1;
      if (!isSyariah) {
        showSyariahRestrictionModal();
        return;
      }

      const isTradeable = await checkSyariahTradeableStatus(data.symbol);
      if (!isTradeable) {
        showSyariahRestrictionModal();
        return;
      }
    }

    const isAutoOrder = [
      SellOrderType.StopLoss,
      SellOrderType.TakeProfit,
    ].includes(data.formType as SellOrderType);
    if (isAutoOrder) {
      const { isDayTrade, symbol: sym } = data;
      const newPortoData = getPortoData(sym, isDayTrade);

      if (newPortoData) {
        setPortoData({
          symbol: sym,
          isDayTrade,
          exerciseable: newPortoData?.exercise_data?.active,
          type: newPortoData?.type,
          initialModalType: 'auto-order',
          initialAutoOrderType: data.formType as SellOrderType,
        });
      }
    } else if (data.orderId) {
      const { orderHistory } = tradingDataStore.getState();
      const order = orderHistory?.data?.find((o) => o.orderid === data.orderId);

      if (order?.amend_action) {
        showTradingModalWithAuth(
          {
            symbol: data.symbol,
            price: Math.floor(data.newPrice),
            orderType:
              order.action === 'BUY'
                ? AUTH_ORDER_TYPE.AMEND_BUY
                : AUTH_ORDER_TYPE.AMEND_SELL,
            orderedLot: order.order_total,
            orderid: data.orderId,
            orderedPrice: order.price_order,
            isDayTrade: order.day_trade_is_day_trade,
            formType: data.formType,
            setToMaxLot: data.setToMaxLot,
          },
          clearPortoAndOrderLabel,
        );
      } else {
        alertMessage({
          content: 'Unable to amend this order',
          alertType: 'snackbar-dark',
          messageType: 'error',
          hasCloseIcon: true,
        });
      }
    } else {
      showTradingModalWithAuth(
        {
          symbol: data.symbol,
          price: Math.floor(data.newPrice),
          orderType: data.orderType,
          formType: data.formType,
          setToMaxLot: data.setToMaxLot,
        },
        clearPortoAndOrderLabel,
      );
    }
  });
};
